import { Directive, EventEmitter, HostListener, inject, Input, Output } from '@angular/core';
import { DeviceInfoService } from '@core/services/device-info.service';
import { AllDeviceInfo, DeviceType } from '@interfaces';

@Directive({
  selector: '[appSwipe]',
  standalone: false,
})
export class SwipeDirective {
  private readonly deviceInfoService = inject(DeviceInfoService);

  @Input() allowedDevices: { device?: DeviceType; browser?: string; os?: string }[] = [];
  @Input() deniedDevices: { device?: DeviceType; browser?: string; os?: string }[] = [];

  @Output() swipeLeft = new EventEmitter<void>();
  @Output() swipeRight = new EventEmitter<void>();

  private swipeCoord: [number, number] = [0, 0];
  private swipeTime: number = new Date().getTime();
  private isHorizontalScroll: boolean = false;

  private deviceInfo: AllDeviceInfo;

  constructor() {
    this.deviceInfo = this.deviceInfoService.getInfo();

    this.deviceInfoService.infoEmitter.subscribe(info => {
      this.deviceInfo = info;
    });
  }

  @HostListener('touchstart', ['$event']) onSwipeStart(event: TouchEvent) {
    if (!this.isDeviceAllowed()) {
      return;
    }

    this.isHorizontalScroll = false;
    this.swipeCoord = [event.touches[0].clientX, event.touches[0].clientY];
    this.swipeTime = new Date().getTime();
  }

  @HostListener('touchmove', ['$event']) onTouchMove(event: TouchEvent) {
    if (!this.isDeviceAllowed()) {
      return;
    }

    const deltaX = event.touches[0].clientX - this.swipeCoord[0];
    const deltaY = event.touches[0].clientY - this.swipeCoord[1];

    if (!this.isHorizontalScroll && Math.abs(deltaX) > Math.abs(deltaY)) {
      this.isHorizontalScroll = true;
    }

    if (this.isHorizontalScroll && event.cancelable) {
      event.preventDefault();
    }
  }

  @HostListener('touchend', ['$event']) onSwipeEnd(event: TouchEvent) {
    if (!this.isDeviceAllowed()) {
      return;
    }

    if (this.isHorizontalScroll) {
      this.detectSwipe(event);
    }
  }

  private detectSwipe(event: TouchEvent): void {
    const coord: [number, number] = [event.changedTouches[0].clientX, event.changedTouches[0].clientY];
    const time = new Date().getTime();
    const direction = [coord[0] - this.swipeCoord[0], coord[1] - this.swipeCoord[1]];
    const duration = time - this.swipeTime;

    if (duration < 1000 && Math.abs(direction[0]) > 30 && Math.abs(direction[0]) > Math.abs(direction[1] * 3)) {
      if (direction[0] < 0) {
        this.swipeLeft.emit();
      } else {
        this.swipeRight.emit();
      }
    }
  }

  private isDeviceAllowed(): boolean {
    if (this.allowedDevices.length > 0) {
      const matchAllowed = this.allowedDevices.some(allowed => this.matchDevice(allowed));
      if (!matchAllowed) {
        return false;
      }
    }

    const matchDenied = this.deniedDevices.some(denied => this.matchDevice(denied));
    if (matchDenied) {
      return false;
    }

    return true;
  }

  private matchDevice(filter: { device?: string; browser?: string; os?: string }): boolean {
    if (filter.device) {
      if (this.deviceInfo.deviceTypeDetected.toUpperCase() !== filter.device.toUpperCase()) {
        return false;
      }
    }

    if (filter.browser) {
      if (this.deviceInfo.deviceInfo.browser.toUpperCase() !== filter.browser.toUpperCase()) {
        return false;
      }
    }

    if (filter.os) {
      if (this.deviceInfo.deviceInfo.os.toUpperCase() !== filter.os.toUpperCase()) {
        return false;
      }
    }

    return true;
  }
}
